// One in-app dialog for the admin maintenance tools, replacing window.confirm
// and window.alert.
//
// Why this exists: Chrome offers "prevent this page from creating additional
// dialogs" after a couple of native popups. Tick it and confirm() stops showing
// anything and simply returns false — so the button does nothing, forever, with
// no error and no clue. That is what made "Repair Buyer Folders" look dead for
// an hour on 2026-08-07. A dialog we render ourselves cannot be suppressed.
//
// The API deliberately mirrors what it replaces, so a call site changes by one
// word and keeps its shape:
//
//   if (!(await confirm({ title, body, items, note }))) return;
//   await notify("Done: moved 3.");
//   const reason = await prompt({ title, label: "Reason" });
//
// `confirm` resolves true/false. `notify` resolves when the reader dismisses it.
// `prompt` resolves the typed string, or null if cancelled — same contract as
// window.prompt, so `|| ""` fallbacks at call sites keep working.
//
// Pass `tone: "danger"` on anything that cannot be undone; the confirm button
// turns red instead of navy so a permanent delete never looks like a routine OK.
//
// Render `dialog` once anywhere in the component that owns the hook.
import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

export function useActionDialog() {
  const [state, setState] = useState(null);
  const resolveRef = useRef(null);

  const close = useCallback((result) => {
    setState(null);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    if (resolve) resolve(result);
  }, []);

  const open = useCallback(
    (opts, kind) =>
      new Promise((resolve) => {
        // Opening a second dialog while one is pending would strand the first
        // promise and hang whichever handler is awaiting it. Settle it first.
        if (resolveRef.current) resolveRef.current(false);
        resolveRef.current = resolve;
        setState({ ...normalize(opts), kind });
      }),
    [],
  );

  const confirm = useCallback((opts) => open(opts, "confirm"), [open]);
  const notify = useCallback((opts) => open(opts, "notify"), [open]);
  const prompt = useCallback((opts) => open(opts, "prompt"), [open]);

  const dialog = state ? <ActionDialog {...state} onClose={close} /> : null;

  return { confirm, notify, prompt, dialog };
}

// Let callers pass a bare string, the way alert() takes one.
function normalize(opts) {
  return typeof opts === "string" ? { body: opts } : opts || {};
}

function ActionDialog({
  kind,
  title,
  body,
  items,
  note,
  confirmLabel,
  tone,
  label,
  defaultValue,
  placeholder,
  readOnly,
  onClose,
}) {
  const isPrompt = kind === "prompt";
  const isConfirm = kind === "confirm" || isPrompt;
  const [text, setText] = useState(defaultValue || "");

  // window.prompt resolves null when cancelled and a string otherwise. Keeping
  // that exact contract means `prompt(...) || ""` at a call site still works.
  const cancelValue = isPrompt ? null : false;

  useEffect(() => {
    // Escape cancels, matching every other dialog in the app.
    const onKey = (e) => {
      if (e.key === "Escape") onClose(cancelValue);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, cancelValue]);

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-4"
      onClick={() => onClose(cancelValue)}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900 text-sm">
            {title || (isConfirm ? "Are you sure?" : "Done")}
          </h3>
          <button
            onClick={() => onClose(cancelValue)}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 max-h-[55vh] overflow-y-auto">
          {body ? (
            <p className="text-sm text-slate-700 whitespace-pre-line">{body}</p>
          ) : null}

          {/* The native popup could only show the first ten before it got
              unwieldy. This one scrolls, so the whole list is reviewable. */}
          {items && items.length ? (
            <ul className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1">
              {items.map((t, i) => (
                <li key={i} className="text-xs text-slate-700">
                  {t}
                </li>
              ))}
            </ul>
          ) : null}

          {isPrompt ? (
            <div className="mt-3">
              {label ? (
                <label
                  htmlFor="action-dialog-input"
                  className="block text-xs font-medium text-slate-600 mb-1"
                >
                  {label}
                </label>
              ) : null}
              <input
                id="action-dialog-input"
                autoFocus
                readOnly={!!readOnly}
                value={text}
                placeholder={placeholder || ""}
                onChange={(e) => setText(e.target.value)}
                // Enter accepts, the way it does in a native prompt.
                onKeyDown={(e) => {
                  if (e.key === "Enter") onClose(text);
                }}
                // A read-only field is there to be copied out of.
                onFocus={readOnly ? (e) => e.target.select() : undefined}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#218FDA]/40 focus:border-[#218FDA]"
              />
            </div>
          ) : null}

          {note ? <p className="mt-3 text-xs text-slate-500">{note}</p> : null}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          {isConfirm ? (
            <button
              onClick={() => onClose(cancelValue)}
              className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
          ) : null}
          <button
            autoFocus={!isPrompt}
            onClick={() => onClose(isPrompt ? text : true)}
            className={`px-3 py-1.5 text-sm font-semibold rounded-lg text-white ${
              tone === "danger"
                ? "bg-red-600 hover:bg-red-700"
                : "bg-[#1E293B] hover:bg-slate-700"
            }`}
          >
            {confirmLabel || (isPrompt ? "OK" : isConfirm ? "Continue" : "OK")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ActionDialog;
